// src/utils/stewardAreas.js
// Pure helpers for the Steward Workspace's area choice (Roadmap #86;
// docs/briefs/2026-10-03-steward-workspace-many-areas.md + addendum 1). No data access here.

// Up to this many areas the page keeps its tabs (R1); above it, one searchable picker (R2).
export const AREA_TABS_MAX = 6;

// Fold a name for matching as you type: case- and accent-insensitive, and any run of punctuation
// or spaces becomes one space, so "povoa de" finds "Póvoa-de-Varzim". (eventsImportParsing.js's
// normaliseForCompare strips punctuation outright, which would lose that match — see
// docs/reuse-backlog.md.)
export function foldForSearch(text) {
  return String(text || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

// The areas whose name contains what was typed (folded both sides). Empty input matches all.
export function matchAreas(areas, query) {
  const q = foldForSearch(query);
  if (!q) return areas || [];
  return (areas || []).filter(a => foldForSearch(a.boundary_name).includes(q));
}

// Where the page opens (R3): the area this person last chose, if it is still offered; else the
// first offered area they hold a steward appointment for (areas arrive sorted by name); else none.
export function pickStartingArea(areas, storedId, ownAppointedIds) {
  const offered = (areas || []).map(a => Number(a.boundary_id));
  if (storedId != null && offered.includes(Number(storedId))) return Number(storedId);
  const own = new Set((ownAppointedIds || []).map(Number));
  const firstOwn = offered.find(id => own.has(id));
  return firstOwn ?? null;
}

// Every steward's name for each area, in a stable, language-aware order (addendum 1, ruling 4).
export function stewardNamesByArea(appointments, locale) {
  const collator = new Intl.Collator(locale || undefined, { sensitivity: 'base' });
  const byArea = {};
  (appointments || []).forEach(({ boundary_id, display_name }) => {
    if (!display_name) return;
    const key = Number(boundary_id);
    (byArea[key] = byArea[key] || []).push(display_name);
  });
  Object.keys(byArea).forEach(k => {
    byArea[k] = [...new Set(byArea[k])].sort(collator.compare);
  });
  return byArea;
}

// "A", "A and B", "A, B and C" in the page's language.
export function joinNames(names, locale) {
  try {
    return new Intl.ListFormat(locale || undefined, { style: 'long', type: 'conjunction' }).format(names);
  } catch {
    return names.join(', ');
  }
}

// The scale band a picker group is headed at: municipality (PT concelho, GB LAD). Architecture
// § Tier: tiers align by scale, not source depth, so PT parishes (70) and GB wards (80) both sit
// below it and join their parent's group (Roadmap #87, picker grouped by municipality).
export const GROUP_TIER = 60;

// The areas in groups, one per municipality: [{ key, heading, areas }]. An area below the band
// joins its parent's group (headed by the parent's name, whether or not the parent is offered);
// any other area heads its own. Within a group the heading area comes first, then by name;
// groups are ordered by heading. Areas carry { boundary_id, boundary_name, tier, parentId,
// parentName }.
export function groupAreasByMunicipality(areas, locale) {
  const collator = new Intl.Collator(locale || undefined, { sensitivity: 'base' });
  const groups = new Map();
  (areas || []).forEach(area => {
    const joinsParent = area.tier != null && area.tier > GROUP_TIER && area.parentId != null;
    const key = joinsParent ? Number(area.parentId) : Number(area.boundary_id);
    const heading = joinsParent ? (area.parentName || area.boundary_name) : area.boundary_name;
    if (!groups.has(key)) groups.set(key, { key, heading, areas: [] });
    const group = groups.get(key);
    if (!joinsParent) group.heading = area.boundary_name;
    group.areas.push(area);
  });
  const list = [...groups.values()];
  list.forEach(g => g.areas.sort((a, b) => {
    const aHead = Number(a.boundary_id) === g.key ? 0 : 1;
    const bHead = Number(b.boundary_id) === g.key ? 0 : 1;
    return aHead - bHead || collator.compare(a.boundary_name, b.boundary_name);
  }));
  return list.sort((a, b) => collator.compare(a.heading, b.heading));
}

// The areas matching what was typed, grouped: an area matches by its own name, or by its group's
// heading, so typing a municipality lists the whole municipality.
export function matchAreaGroups(areas, query, locale) {
  const q = foldForSearch(query);
  const groups = groupAreasByMunicipality(areas, locale);
  if (!q) return groups;
  return groups
    .map(g => (foldForSearch(g.heading).includes(q)
      ? g
      : { ...g, areas: matchAreas(g.areas, query) }))
    .filter(g => g.areas.length > 0);
}

// Whole groups only, up to max areas — never half a municipality, and never a gap: listing stops
// at the first group that does not fit, so what shows is an unbroken run from the top of the
// alphabet (a skipped municipality between two shown ones would read as "not covered"). The first
// group always shows, however large. Returns the groups shown and how many areas are left unshown.
export function capGroups(groups, max) {
  const shown = [];
  let count = 0;
  for (const g of groups || []) {
    if (shown.length > 0 && count + g.areas.length > max) break;
    shown.push(g);
    count += g.areas.length;
  }
  const total = (groups || []).reduce((n, g) => n + g.areas.length, 0);
  return { shown, hidden: total - count };
}

// The areas a steward holds by appointment, among those offered: an offered area whose parent is
// not also offered (everything inside an appointment is offered with it, 20261004f). Used for
// where the page opens (pickStartingArea).
export function appointedAmong(areas) {
  const offered = new Set((areas || []).map(a => Number(a.boundary_id)));
  return (areas || [])
    .filter(a => a.parentId == null || !offered.has(Number(a.parentId)))
    .map(a => Number(a.boundary_id));
}

// The countries an admin's "National" entries cover (Roadmap #87, addendum 2): every country among
// the offered areas, plus every country that already has a national listing, so a country whose
// listings are all national stays reachable. A missing code is dropped (admin_boundaries
// .country_code is nullable). Upper case, sorted.
export function countriesOf(areas, extraCodes = []) {
  const codes = [
    ...(areas || []).map(a => a.countryCode),
    ...(extraCodes || [])
  ].filter(c => typeof c === 'string' && /^[A-Za-z]{2}$/.test(c)).map(c => c.toUpperCase());
  return [...new Set(codes)].sort();
}

// A country's name in the page's language, from the browser's locale data. Falls back to the
// bare code where the browser cannot name it, as joinNames falls back to a plain join.
export function countryName(code, locale) {
  try {
    return new Intl.DisplayNames([locale || 'en'], { type: 'region' }).of(code) || code;
  } catch {
    return code;
  }
}

// The listing form's area choice when it means "this country's national list", not an area.
export const NATIONAL = 'national';

// What the listing form writes about where a listing is, and where it is afterwards (Roadmap #87,
// R5 and addendum 2). The database holds exactly one of boundary_id or national_country_code, so
// an unmark sets both in the same write, and a national create sends no boundary_id at all.
// A group-linked listing's area follows its group, so the form never moves it.
// areaId / startArea: an area id, or NATIONAL. Returns { fields, where }: fields to merge into the
// write; where = an area id, or { national: country }.
export function listingAreaWrite({ isEdit, isNational, isLinked, country, areaId, startArea, listingAreaId }) {
  const toArea = !isLinked && areaId !== NATIONAL && areaId !== startArea;
  let fields = {};
  if (isEdit) {
    if (toArea) fields = isNational ? { boundary_id: areaId, national_country_code: null } : { boundary_id: areaId };
  } else {
    fields = areaId === NATIONAL ? { national_country_code: country } : { boundary_id: areaId };
  }
  const where = areaId === NATIONAL || (isNational && !toArea)
    ? { national: country }
    : (isLinked ? Number(listingAreaId) : areaId);
  return { fields, where };
}
